import { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  LayoutDashboard,
  CalendarDays,
  ClipboardList,
  User,
  Clock,
  FileText,
  Umbrella,
  ShoppingCart,
  FileStack,
  Wallet,
} from 'lucide-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useAppMode } from '@/features/settings/appMode';

interface SubItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string; size?: number }>;
}

interface Tab {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string; size?: number }>;
  matchPrefix?: string;
  subItems?: SubItem[];
}

const TABS: Tab[] = [
  { to: '/dashboard', label: 'ראשי', icon: LayoutDashboard },
  { to: '/schedule', label: 'סידור', icon: CalendarDays },
  { to: '/reports/time', label: 'שעות', icon: Clock, matchPrefix: '/reports/time' },
  {
    // `to` points at the sub-item that survives office filtering (below) so the
    // group isn't filtered away entirely for office workers — see hideForOffice.
    to: '/personal/purchase-requests',
    label: 'טפסים',
    icon: FileStack,
    subItems: [
      { to: '/reports/daily', label: 'יומן עבודה', icon: ClipboardList },
      { to: '/personal/purchase-requests', label: 'בקשות רכש', icon: ShoppingCart },
    ],
  },
  {
    to: '/personal/payslips',
    label: 'אישי',
    icon: User,
    // No matchPrefix: with purchase-requests moved to טפסים, אישי's remaining
    // /personal paths are exactly its subItems, so the subItems.some() check
    // in isTabActive is sufficient — a '/personal' matchPrefix would also
    // match /personal/purchase-requests and wrongly highlight both tabs.
    subItems: [
      { to: '/personal/payslips', label: 'תלושים', icon: FileText },
      { to: '/personal/vacation', label: 'חופשה', icon: Umbrella },
      { to: '/personal/petty-cash', label: 'קופה קטנה', icon: Wallet },
    ],
  },
];

// Simple mode: ראשי / שעות / אישי — no schedule, daily report or warehouse.
const SIMPLE_TABS: Tab[] = [
  { to: '/dashboard', label: 'ראשי', icon: LayoutDashboard },
  { to: '/reports/time', label: 'שעות', icon: Clock, matchPrefix: '/reports/time' },
  {
    to: '/personal/payslips',
    label: 'אישי',
    icon: User,
    matchPrefix: '/personal',
    subItems: [
      { to: '/schedule', label: 'הסידור שלי', icon: CalendarDays },
      { to: '/personal/payslips', label: 'תלושים', icon: FileText },
      { to: '/personal/vacation', label: 'חופשה', icon: Umbrella },
      { to: '/personal/petty-cash', label: 'קופה קטנה', icon: Wallet },
      { to: '/personal/purchase-requests', label: 'בקשות רכש', icon: ShoppingCart },
    ],
  },
];

export default function BottomTabBar() {
  const { workerContext } = useAuth();
  const { mode } = useAppMode();
  const isOffice = workerContext?.workerType === 'office';
  const location = useLocation();
  const navigate = useNavigate();
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);

  const modeTabs = mode === 'simple' ? SIMPLE_TABS : TABS;
  const hideForOffice = (to: string) => to === '/schedule' || to === '/reports/daily';
  const visibleTabs = isOffice
    ? modeTabs
        .filter((t) => !hideForOffice(t.to))
        .map((t) =>
          t.subItems ? { ...t, subItems: t.subItems.filter((s) => !hideForOffice(s.to)) } : t
        )
    : modeTabs;
  const menuRef = useRef<HTMLDivElement>(null);

  const isTabActive = (tab: Tab) => {
    if (tab.subItems?.some((s) => location.pathname.startsWith(s.to))) return true;
    if (tab.matchPrefix) {
      return location.pathname.startsWith(tab.matchPrefix);
    }
    return location.pathname === tab.to;
  };

  const handleTabClick = (e: React.MouseEvent, tab: Tab, index: number) => {
    if (tab.subItems) {
      e.preventDefault();
      setOpenMenuIndex(openMenuIndex === index ? null : index);
    }
  };

  const handleSubItemClick = (to: string) => {
    navigate(to);
    setOpenMenuIndex(null);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuIndex(null);
      }
    };

    if (openMenuIndex !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openMenuIndex]);

  return (
    <nav
      ref={menuRef}
      className="bg-surface border-line fixed inset-x-0 bottom-0 z-40 border-t"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {/* Popup Menus */}
      {visibleTabs.map((tab, index) => {
        if (!tab.subItems || openMenuIndex !== index) return null;

        return (
          <div
            key={`menu-${index}`}
            className="animate-in slide-in-from-bottom-2 absolute right-0 bottom-full left-0 mb-2 px-4 duration-200"
          >
            <div className="rounded-card bg-surface mx-auto max-w-sm space-y-1 overflow-hidden p-2 shadow-[0_1px_2px_rgba(0,0,0,0.06),0_8px_24px_rgba(0,0,0,0.12)]">
              {tab.subItems.map((subItem) => {
                const SubIcon = subItem.icon;
                const isSubActive = location.pathname === subItem.to;
                return (
                  <button
                    key={subItem.to}
                    onClick={() => handleSubItemClick(subItem.to)}
                    className={clsx(
                      'rounded-control flex min-h-[52px] w-full items-center gap-3 px-4 py-3 text-right text-base font-bold transition-[color,background-color,transform] duration-150 active:scale-[0.96]',
                      isSubActive ? 'bg-brand-soft text-brand' : 'text-ink-2 hover:bg-surface-soft'
                    )}
                  >
                    <SubIcon size={20} className={isSubActive ? 'text-brand' : 'text-muted'} />
                    <span className="flex-1">{subItem.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Bottom Tabs */}
      <div className="flex items-stretch">
        {visibleTabs.map((tab, index) => {
          const isActive = isTabActive(tab);
          const Icon = tab.icon;
          const hasMenu = !!tab.subItems;
          const isMenuOpen = openMenuIndex === index;

          if (hasMenu) {
            return (
              <button
                key={tab.to}
                onClick={(e) => handleTabClick(e, tab, index)}
                className={clsx(
                  'relative flex min-h-[56px] flex-1 flex-col items-center justify-center gap-1 px-0.5 py-2 transition-colors duration-150 select-none',
                  isActive || isMenuOpen ? 'text-brand' : 'text-muted active:text-ink-2'
                )}
              >
                {(isActive || isMenuOpen) && (
                  <span className="bg-brand absolute inset-x-3 top-0 h-0.5 rounded-b-full" />
                )}
                <Icon size={22} />
                <span
                  className={clsx(
                    'text-[10px] leading-tight',
                    isActive || isMenuOpen ? 'font-bold' : 'font-semibold'
                  )}
                >
                  {tab.label}
                </span>
              </button>
            );
          }

          return (
            <NavLink
              key={tab.to}
              to={tab.to}
              className={clsx(
                'relative flex min-h-[56px] flex-1 flex-col items-center justify-center gap-1 px-0.5 py-2 transition-colors duration-150 select-none',
                isActive ? 'text-brand' : 'text-muted active:text-ink-2'
              )}
            >
              {isActive && (
                <span className="bg-brand absolute inset-x-3 top-0 h-0.5 rounded-b-full" />
              )}
              <Icon size={22} />
              <span
                className={clsx(
                  'text-[10px] leading-tight',
                  isActive ? 'font-bold' : 'font-semibold'
                )}
              >
                {tab.label}
              </span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
